import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { usePathname } from './usePathname';
import { useQueryParams } from './useQueryParams';

type StateResult<T> = {
  values: T[];
  add: (value: T) => void;
  remove: (value: T) => void;
  clear: () => void;
};

export function useMultiOptionQueryParam<T extends string>({
  key,
}: {
  key: string;
}): StateResult<T> {
  const router = useRouter();
  const pathname = usePathname();
  const query = useQueryParams();

  const values = Array.isArray(router.query[key])
    ? router.query[key]
    : router.query[key] === undefined
      ? []
      : ([router.query[key]] as string[]);

  const add = useCallback(
    (value: T) => {
      const url = {
        pathname,
        query: {
          ...query,
          [key]: [...values, value],
        },
      };

      router.push(url, undefined, { shallow: true });
    },
    [router, pathname, query],
  );

  const remove = useCallback(
    (value: T) => {
      const url = {
        pathname,
        query: {
          ...query,
          [key]: values.filter((v) => v !== value),
        },
      };

      router.push(url, undefined, { shallow: true });
    },
    [router, pathname, query],
  );

  const clear = useCallback(() => {
    const newQuery = { ...query };
    delete newQuery[key as keyof typeof newQuery];

    const url = {
      pathname,
      query: newQuery,
    };

    router.push(url, undefined, { shallow: true });
  }, [router, pathname, query]);

  return {
    values: values as T[],
    add,
    remove,
    clear,
  };
}
